import React, { useEffect, useState } from 'react';
import {
  Device,
  EnergyInverter,
  useEnergyInverterDetailQuery,
  useEnergyInverterDetailUpdatesSubscription,
} from '../../../../types/generated-types';
import DeviceLoadingSkeleton from '../shared-ui/deviceLoadingSkeleton';
import { formatDeviceMetric } from '../../../utils/formatters';
import { DeviceInformation } from '../shared-ui/device-info';
import { PanelFrame, ViewMode } from '../shared-ui/panel-frame';
import { Box, Grid, Typography } from '@mui/material';
import moment from 'moment';
import { updateCacheFromSubscriptionEvent } from '../../../../helpers/subscriptionUtils';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import { EnergyControllerComponent } from './energy-controller';
import { EnergyRectifierComponent } from './energy-rectifier';

interface EnergyInverterProps {
  id: string;
}

export const EnergyInverterComponent: React.FC<EnergyInverterProps> = ({
  id,
}) => {
  const [device, setDevice] = useState<Partial<EnergyInverter>>();
  const [deviceInfo, setDeviceInfo] = useState<DeviceInformation[]>([]);

  const { data } = useEnergyInverterDetailQuery({
    variables: { id },
  });

  useEnergyInverterDetailUpdatesSubscription({
    variables: { id },
    fetchPolicy: 'no-cache',
    onData: updateCacheFromSubscriptionEvent,
  });

  useEffect(() => {
    if (data?.energyInverterById) {
      setDevice(data.energyInverterById as Partial<EnergyInverter>);
    }
  }, [data]);

  useEffect(() => {
    if (device) {
      const info: DeviceInformation[] = [
        {
          'Device Identification': {
            'Device Name': device.name ?? 'Unnamed Energy Inverter',
            'Device ID': device.deviceId ?? 'Unidentified',
            'Device Type': device.typeDisplayName ?? 'Unknown',
            'Last Updated': moment(device.timestamp).fromNow(),
          },
          'Power Metrics': {
            'Power Rating': formatDeviceMetric(device.powerRating),
            'Power Output': formatDeviceMetric(device.powerOutput),
            'Reactive Power': formatDeviceMetric(device.reactivePower),
            Efficiency: formatDeviceMetric(device.efficiency),
          },
          'Input Power': {
            'Battery Input Power': formatDeviceMetric(device.powerInputBattery),
            'Kinetic Input Power': formatDeviceMetric(device.powerInputKinetic),
          },
          'Output Characteristics': {
            'Output Voltage': formatDeviceMetric(device.voltageOutput),
            'Output Frequency': formatDeviceMetric(device.frequency),
            Temperature: formatDeviceMetric(device.inverterTemperature),
          },
          'Battery Management': {
            'Charge Rate': formatDeviceMetric(device.chargeRate),
            'Discharge Rate': formatDeviceMetric(device.dischargeRate),
          },
          'System Status': {
            'Availability Status':
              device.availabilityStatus?.value ?? 'Unknown',
            'Power State': device.powerState?.value ? 'On' : 'Off',
            'Update Status': `${device.updateStatus ?? 'Unknown'}`,
            'Is Configured': device.isConfigured ? 'Yes' : 'No',
            'Is Powered On': device.isPoweredOn ? 'Yes' : 'No',
            'Has Pending Updates': device.hasPendingUpdates ? 'Yes' : 'No',
            'Is Controlled': device.isControlled ? 'Yes' : 'No',
          },
          'System Integration': {
            'Controller ID': device.controllerId ?? 'Not assigned',
            'Rectifier ID': device.rectifierId ?? 'Not assigned',
          },
        },
      ];
      setDeviceInfo(info);
    }
  }, [device]);

  if (!device) {
    return <DeviceLoadingSkeleton size="large" />;
  }

  return (
    <PanelFrame
      title="Energy Inverter"
      iconType="mdi:transmission-tower-export"
      device={device as Partial<Device>}
      infoEntries={deviceInfo}
      viewMode={ViewMode.NORMAL}
      editingEnabled={false}
    >
      <Box sx={{ p: 2 }}>
        <Stack
          direction={'row'}
          justifyContent={'space-between'}
          divider={<Divider orientation="vertical" flexItem />}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <Typography
                variant="subtitle2"
                color="textSecondary"
                align="center"
              >
                Power Output
              </Typography>
              <Typography variant="h6" align="center">
                {formatDeviceMetric(device.powerOutput)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography
                variant="subtitle2"
                color="textSecondary"
                align="center"
              >
                Efficiency
              </Typography>
              <Typography variant="h6" align="center">
                {formatDeviceMetric(device.efficiency)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography
                variant="subtitle2"
                color="textSecondary"
                align="center"
              >
                Status
              </Typography>
              <Typography variant="h6" align="center">
                {device.powerState?.value ? 'On' : 'Off'}
              </Typography>
            </Grid>
          </Grid>
          {device.controllerId ? (
            <EnergyControllerComponent id={device.controllerId} />
          ) : null}
          {device.rectifierId ? (
            <EnergyRectifierComponent id={device.rectifierId} />
          ) : null}
        </Stack>
      </Box>
    </PanelFrame>
  );
};
